import { defineComponent, PropType, reactive, watch } from '@vue/composition-api';
import { ConnectionStatus } from '@/views/connections/connections-types';
import {
  HaileyApiServiceInfo,
  HaileyApiServiceMutationResult,
  disableHaileyApi,
  enableHaileyApi,
  updateHaileyApi,
} from '@/functions/services-functions';
import {
  alertDialogue,
  confirmDialogue,
  generalError,
  inputDialogue,
} from '@/functions/system-modal-functions';
import { i18n } from '@/utilities/vue-i18n';
import { ModalIconType } from '../system-modals/system-modals';

type SettingsType = {
  connection: HaileyApiServiceInfo;
  loading: boolean;
  company: number;
};

const isMutationError = (
  service: HaileyApiServiceMutationResult
): service is { error: 'access-token-conflict' } => 'error' in service;

export default defineComponent({
  props: {
    connection: {
      required: true,
      type: Object as PropType<HaileyApiServiceInfo>,
    },
    companyId: {
      required: false,
      type: Number,
    },
    allowDelete: {
      required: false,
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const settings = reactive<SettingsType>({
      connection: { ...props.connection },
      loading: props.loading,
      company: props.companyId ?? +context.root.$route.params.company,
    });

    watch(
      () => props.connection,
      value => {
        settings.connection = { ...value };
      },
      { deep: true }
    );

    watch(
      () => props.companyId,
      value => {
        settings.company = value ?? +context.root.$route.params.company;
      }
    );

    watch(
      () => props.loading,
      value => {
        settings.loading = value ?? settings.loading;
      }
    );

    const promptAccessToken = () =>
      inputDialogue({
        title: 'Hailey API',
        message: i18n.t('components.hailey-api-connection.enter-access-token'),
        validation: input => input?.trim().length > 0 && input.trim().length <= 255,
        inputType: 'password',
      });

    const mutate = async (operation: typeof enableHaileyApi | typeof updateHaileyApi) => {
      if (settings.loading) {
        return;
      }

      settings.loading = true;

      const dialogue = await promptAccessToken();

      if (dialogue.confirmed) {
        const service = await operation(settings.company, dialogue.input);

        if (service && !isMutationError(service)) {
          settings.connection = service;
          context.emit('updated', service);
        } else if (service) {
          await alertDialogue(
            'Hailey API',
            i18n.t('components.hailey-api-connection.error-access-token-conflict') as string
          );
        } else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    const disable = async () => {
      if (!props.allowDelete || settings.loading) {
        return;
      }

      settings.loading = true;

      const confirmed = await confirmDialogue(
        'Hailey API',
        i18n.t('components.hailey-api-connection.disable-integration'),
        ModalIconType.warning
      );

      if (confirmed) {
        if (await disableHaileyApi(settings.company)) {
          context.emit('updated', null);
        } else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    return {
      settings,
      ConnectionStatus,
      enable: () => mutate(enableHaileyApi),
      update: () => mutate(updateHaileyApi),
      disable,
    };
  },
});
